import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IUseChatHandlers,
  IUseCheckInitialUnreadChannels,
  IUseCheckUnreadChannel,
  IUseInitializeStreamClientConnection,
  IUseNewChannel,
  IUseSetInitialChannels,
  IUseStreamChatUnavailableNotification,
} from '@core/chat/interfaces';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useChatState } from '@core/chat/context';

import { useRedirectToGlobalChat } from '@core/chat/hooks/channel';

import {
  checkHasUnreadPrivateChannel,
  checkIsStreamChannelUnread,
} from '../helpers';
import { EventHandler, StreamChat } from 'stream-chat';
import { DefaultStreamChatGenerics as StreamChatGenerics } from 'stream-chat-react/dist/types/types';
import { getCatchErrorMessage } from '@helpers/error';
import { MytaverseLogger } from '@helpers/logger';
import {
  useRedirectToPrivateChat,
  useStreamChannels,
} from '@core/chat/StreamChat/hooks';
import ChannelService from '@core/chat/services/ChannelService';
import { IEventChannel } from '@core/chat/StreamChat/interfaces';
import { useSound } from '@hooks/media';
import NotificationSound from '@public/sounds/notification.wav';
import { ChatMessageType } from '@core/chat/components/ChatNotification';
import useNotificationMessage from '@core/chat/hooks/useNotificationMessage';
import { useNotificationContext } from '@providers/NotificationProvider';
import { sendCustomSentryBreadcrumb } from '@helpers/sentry';
import {
  checkChannelFormCurrentEvent,
  checkIsEventChannel,
  checkIsRoomChannel,
  getPrivateChannelId,
} from '@core/chat/helpers';

import { useNotification } from '@components/Notification';
import { NOTIFICATION_TYPES } from '@components/Notification/constants';
import { getNotification } from '@components/Notification/helpers';

const useStreamChatHandlers = ({
  currentEvent,
  streamChatToken,
  isStreamChatUnavailable: isUnavailableOnBackend = false,
}: IUseChatHandlers) => {
  const { user, configs } = useMytaverse();
  const { streamChatMemberId, participants } = useMytaverseEvent();
  const {
    open: isOpenChat,
    streamClient,
    setStreamClient,
    eventStreamChannel,
    eventRoomStreamChannel,
    setEventStreamChannel,
    privateChannels,
    setPrivateChannels,
    addPrivateChannel,
    setInitialLoading,
    setIsUnreadEventChannel,
    setIsUnreadPrivateChannel,
    setEventRoomStreamChannel,
    setIsUnreadEventRoomChannel,
    isStreamChatUnavailable,
    setIsStreamChatUnavailable,
  } = useChatState();

  const userId = streamChatMemberId || '';
  const currentEventId = currentEvent?.eventId || '';

  // Follows the backend verdict in both directions, so the flag does not stay
  // stale when the participant enters an experience whose chat is healthy.
  useEffect(() => {
    if (isStreamChatUnavailable !== isUnavailableOnBackend) {
      setIsStreamChatUnavailable(isUnavailableOnBackend);
    }
  }, [isUnavailableOnBackend, currentEventId]);

  useInitializeStreamClientConnection({
    streamChatToken,
    setStreamClient,
    setIsStreamChatUnavailable,
    user,
    streamChatMemberId: userId,
    configs,
    isUnavailableOnBackend,
  });

  useStreamChatUnavailableNotification({
    isStreamChatUnavailable,
    currentEventId,
  });

  useSetInitialChannels({
    streamClient,
    setEventStreamChannel,
    setPrivateChannels,
    setEventRoomStreamChannel,
    setIsStreamChatUnavailable,
    currentEventId,
    userId,
  });

  useCheckInitialUnreadChannels({
    eventChannel: eventStreamChannel,
    eventRoomChannel: eventRoomStreamChannel,
    privateChannels,
    setInitialLoading,
    setIsUnreadEventChannel,
    setIsUnreadPrivateChannel,
    setIsUnreadEventRoomChannel,
  });

  useCheckUnreadChannel({
    privateChannels,
    setIsUnreadEventChannel,
    setIsUnreadPrivateChannel,
    setIsUnreadEventRoomChannel,
    streamClient,
    userId,
    currentEventId,
    isOpenChat,
    participants,
  });

  useNewChannel({
    streamClient,
    hasEventStreamChannel: !!eventStreamChannel,
    setEventStreamChannel,
    addPrivateChannel,
    currentEventId,
    userId,
  });
};

/**
 * Opens the Stream Chat websocket for the current participant and closes it again on teardown.
 *
 * @param props.streamChatToken - Per-event token; a new one restarts the connection.
 * @param props.setStreamClient - Publishes the connected client to the chat store.
 * @param props.setIsStreamChatUnavailable - Flips the degraded-chat flag on connect success/failure.
 * @param props.user - Only its id is read, to reconnect when the account changes.
 * @param props.streamChatMemberId - Stream Chat user id to connect as.
 * @param props.configs - Carries the Stream Chat API key.
 * @param props.isUnavailableOnBackend - Skips connecting when the backend already reported chat down.
 */
const useInitializeStreamClientConnection = ({
  streamChatToken,
  setStreamClient,
  setIsStreamChatUnavailable,
  user,
  streamChatMemberId,
  configs,
  isUnavailableOnBackend,
}: IUseInitializeStreamClientConnection) => {
  useEffect(() => {
    // No point in waiting on Stream Chat timeouts when the backend already
    // failed to reach it while preparing this event (MV-1856).
    if (!streamChatMemberId || !streamChatToken || isUnavailableOnBackend) {
      return;
    }

    let cancelled = false;
    const apiKey = configs?.streamChat?.tokens?.apiKey || '';
    const client = new StreamChat<StreamChatGenerics>(apiKey);

    const disconnect = () => {
      client.disconnectUser().catch((error: unknown) => {
        MytaverseLogger.error(
          `Failed to disconnect from Stream Chat: ${getCatchErrorMessage(
            error,
          )}`,
          { streamChatMemberId },
        );
      });
    };

    const initializeStreamClientConnection = async () => {
      try {
        const chatUser = {
          id: streamChatMemberId,
          name: streamChatMemberId,
        };
        await client.connectUser(chatUser, streamChatToken);

        if (cancelled) {
          disconnect();
          return;
        }

        setStreamClient(client);
        setIsStreamChatUnavailable(false);
      } catch (error: unknown) {
        MytaverseLogger.error(
          `Failed to connect to Stream Chat: ${getCatchErrorMessage(error)}`,
          { streamChatMemberId },
        );

        if (!cancelled) {
          setIsStreamChatUnavailable(true);
        }
      }
    };

    initializeStreamClientConnection();

    return () => {
      cancelled = true;

      disconnect();
      useChatState.getState().resetState();
      setStreamClient(null);
    };
  }, [user?.id, streamChatToken, streamChatMemberId, isUnavailableOnBackend]);
};

/**
 * Tells the participant once per experience that chat is down, so a Stream Chat
 * outage reads as a missing feature instead of a broken hub (MV-1856).
 */
const useStreamChatUnavailableNotification = ({
  isStreamChatUnavailable,
  currentEventId,
}: IUseStreamChatUnavailableNotification) => {
  const { t: translate } = useTranslation();
  const { showNotification } = useNotification();
  const { isEventRun } = useNotificationContext();
  const notifiedEventIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Waiting for the experience to actually run: the flag arrives with the
    // event data, minutes before the participant sees anything.
    if (!isStreamChatUnavailable || !currentEventId || !isEventRun) {
      return;
    }

    if (notifiedEventIdRef.current === currentEventId) {
      return;
    }

    notifiedEventIdRef.current = currentEventId;

    showNotification(
      getNotification({
        title: translate('notifications.chatUnavailableTitle'),
        message: translate('notifications.chatUnavailable'),
        type: NOTIFICATION_TYPES.WARNING,
        // Chat is gone silently once dismissed for good, so this one is not
        // the participant's to switch off.
        withDismissButton: false,
      }),
    );
  }, [isStreamChatUnavailable, currentEventId, isEventRun]);
};

const useSetInitialChannels = ({
  streamClient,
  setEventStreamChannel,
  setPrivateChannels,
  setIsStreamChatUnavailable,
  currentEventId,
  userId,
  setEventRoomStreamChannel,
}: IUseSetInitialChannels) => {
  const {
    getEventStreamChannel,
    getMapEventChannels,
    getEventRoomStreamChannel,
  } = useStreamChannels();
  const { currentRoom } = useMytaverseEvent();

  useEffect(() => {
    if (!streamClient || !currentEventId || !userId) {
      return;
    }

    const setInitialChannels = async () => {
      const eventStreamChannel = await getEventStreamChannel({
        streamClient,
        userId,
        eventId: currentEventId,
      });

      setEventStreamChannel(eventStreamChannel);

      const eventRoomStreamChannel = await getEventRoomStreamChannel({
        streamClient,
        userId,
        eventId: currentEventId,
        roomId: currentRoom?.roomId,
      });

      setEventRoomStreamChannel(eventRoomStreamChannel);

      // Our own API, not Stream Chat: losing the private-channel list must not
      // be reported to the participant as a chat outage (MV-1856).
      let eventChannels: IEventChannel[] = [];

      try {
        eventChannels = await ChannelService.getEventChannels(currentEventId);
      } catch (error: unknown) {
        MytaverseLogger.error(
          `Failed to load event chat channels: ${getCatchErrorMessage(error)}`,
          { eventId: currentEventId },
        );
      }

      const privateChannels = await getMapEventChannels(
        streamClient,
        eventChannels,
        userId,
      );

      setPrivateChannels(privateChannels.filter(({ channel }) => channel));
    };

    setInitialChannels()
      .then(() => {
        if (useChatState.getState().isStreamChatUnavailable) {
          setIsStreamChatUnavailable(false);
        }
      })
      .catch((error: unknown) => {
        MytaverseLogger.error(
          `Failed to load Stream Chat channels: ${getCatchErrorMessage(error)}`,
          { eventId: currentEventId },
        );
        setIsStreamChatUnavailable(true);
      });
  }, [streamClient, currentEventId, userId, currentRoom]);
};

const useCheckInitialUnreadChannels = ({
  eventChannel,
  eventRoomChannel,
  privateChannels,
  setInitialLoading,
  setIsUnreadEventChannel,
  setIsUnreadPrivateChannel,
  setIsUnreadEventRoomChannel,
}: IUseCheckInitialUnreadChannels) => {
  useEffect(() => {
    const isEventChannelUnread = checkIsStreamChannelUnread(eventChannel);

    setIsUnreadEventChannel(isEventChannelUnread);

    const isEventRoomChannelUnread =
      checkIsStreamChannelUnread(eventRoomChannel);

    setIsUnreadEventRoomChannel(isEventRoomChannelUnread);

    if (!privateChannels.length) {
      setInitialLoading(false);
      return;
    }

    const hasUnreadPrivateChannel =
      checkHasUnreadPrivateChannel(privateChannels);

    setIsUnreadPrivateChannel(hasUnreadPrivateChannel);
    setInitialLoading(false);
  }, [eventChannel, privateChannels, eventRoomChannel]);
};

const useCheckUnreadChannel = ({
  privateChannels,
  setIsUnreadEventChannel,
  setIsUnreadPrivateChannel,
  setIsUnreadEventRoomChannel,
  streamClient,
  userId,
  currentEventId,
  isOpenChat,
  participants,
}: IUseCheckUnreadChannel) => {
  const [play] = useSound(NotificationSound, {
    volume: 0.15,
  });
  const { showChatNotification } = useNotificationMessage();
  const redirectToPrivateChat = useRedirectToPrivateChat();
  const redirectToGlobalChat = useRedirectToGlobalChat();
  const { isEventRun } = useNotificationContext();
  const { currentRoom, isHideWebUIExceptAssistant, isHideAllExceptTopBar } =
    useMytaverseEvent();
  const { showNotification } = useNotification();
  const { t: translate } = useTranslation();

  useEffect(() => {
    if (!streamClient) {
      return;
    }

    const handleNewMessage: EventHandler<StreamChatGenerics> = (evt) => {
      const participant = participants?.find(
        (participant) =>
          participant.streamChatMemberId === evt?.message?.user?.id ||
          participant.participantId === evt?.message?.user?.id,
      );

      if (evt?.message?.user && participant) {
        if (participant?.fullName) {
          evt.message.user.name = participant?.fullName;
        } else if (participant?.name) {
          evt.message.user.name = participant?.name;
        } else if (participant?.firstName || participant?.lastName) {
          if (participant?.firstName) {
            evt.message.user.name = participant?.firstName;
          }

          if (participant?.lastName) {
            evt.message.user.name += ` ${participant?.lastName}`;
          }
        }
      }

      if (!participant) {
        sendCustomSentryBreadcrumb({
          message:
            'Stream chat error unable to find participant data for user = ' +
            evt?.message?.user?.id,
        });
      }
      const streamChannelId = evt.channel_id || '';
      const readUserId = evt.user?.id || '';

      const isEventRoomChannel = checkIsRoomChannel(
        streamChannelId,
        currentRoom?.roomId as string,
      );

      if (isEventRoomChannel) {
        if (readUserId !== userId) {
          play();
          showNotification(
            getNotification({
              title: translate('notifications.broadcast'),
              message: evt.message?.text as string,
              type: NOTIFICATION_TYPES.SUCCESS,
              closeManually: true,
              withDismissButton: false,
            }),
          );
          setIsUnreadEventRoomChannel(true);
        }

        return;
      }

      const isCurrentUser = readUserId === userId;

      if (isCurrentUser || isOpenChat) {
        return;
      }

      const isCurrentEvent = checkChannelFormCurrentEvent(
        streamChannelId,
        currentEventId,
      );

      if (!isCurrentEvent) {
        return;
      }

      const isEventChannel = checkIsEventChannel(
        streamChannelId,
        currentEventId,
      );

      const isHideUi =
        isHideWebUIExceptAssistant.flag || isHideAllExceptTopBar.flag;

      if (!isEventRun || isHideUi) {
        return;
      }

      if (isEventChannel) {
        setIsUnreadEventChannel(true);
        play();
        showChatNotification({
          message: evt.message as ChatMessageType,
          onReply: redirectToGlobalChat,
        });
      } else {
        setIsUnreadPrivateChannel(true);
        play();
        showChatNotification({
          message: evt.message as ChatMessageType,
          onReply: redirectToPrivateChat,
        });
      }
    };

    const handleReadMessage: EventHandler<StreamChatGenerics> = (evt) => {
      const readUserId = evt.user?.id || '';
      const streamChannelId = evt.channel_id || '';
      const isCurrentUser = readUserId === userId;

      if (!isCurrentUser) {
        return;
      }

      const isEventRoomChannel = checkIsRoomChannel(
        streamChannelId,
        currentRoom?.roomId as string,
      );

      if (isEventRoomChannel) {
        setIsUnreadEventRoomChannel(false);
      }

      const isCurrentEvent = checkChannelFormCurrentEvent(
        streamChannelId,
        currentEventId,
      );

      if (!isCurrentEvent) {
        return;
      }

      const isEventChannel = checkIsEventChannel(
        streamChannelId,
        currentEventId,
      );

      if (isEventChannel) {
        setIsUnreadEventChannel(false);
      } else {
        const hasUnreadPrivateChannel =
          checkHasUnreadPrivateChannel(privateChannels);

        setIsUnreadPrivateChannel(hasUnreadPrivateChannel);
      }
    };

    const handleTruncateChannel: EventHandler<StreamChatGenerics> = (evt) => {
      const streamChannelId = evt.channel_id || '';

      const isEventRoomChannel = checkIsRoomChannel(
        streamChannelId,
        currentRoom?.roomId as string,
      );

      if (isEventRoomChannel) {
        setIsUnreadEventRoomChannel(false);
      }

      const isCurrentEvent = checkChannelFormCurrentEvent(
        streamChannelId,
        currentEventId,
      );

      if (!isCurrentEvent) {
        return;
      }

      const isEventChannel = checkIsEventChannel(
        streamChannelId,
        currentEventId,
      );

      if (isEventChannel) {
        setIsUnreadEventChannel(false);
        return;
      }

      const hasUnreadPrivateChannel =
        checkHasUnreadPrivateChannel(privateChannels);

      setIsUnreadPrivateChannel(hasUnreadPrivateChannel);
    };

    streamClient.on('message.new', handleNewMessage);
    streamClient.on('message.read', handleReadMessage);
    streamClient.on('channel.truncated', handleTruncateChannel);

    return () => {
      streamClient.off('message.new', handleNewMessage);
      streamClient.off('message.read', handleReadMessage);
      streamClient.off('channel.truncated', handleTruncateChannel);
    };
  }, [
    currentEventId,
    privateChannels,
    streamClient,
    userId,
    isOpenChat,
    isEventRun,
    participants,
    isHideWebUIExceptAssistant.flag,
    isHideAllExceptTopBar.flag,
    currentRoom?.roomId,
  ]);
};

const useNewChannel = ({
  streamClient,
  hasEventStreamChannel,
  setEventStreamChannel,
  addPrivateChannel,
  currentEventId,
  userId,
}: IUseNewChannel) => {
  const { getEventStreamChannel, getMapEventChannel } = useStreamChannels();

  useEffect(() => {
    if (!streamClient || !currentEventId || !userId) {
      return;
    }

    const newChannelSubscription = streamClient.on(
      'notification.added_to_channel',
      async (event) => {
        const streamChannelId = event.channel_id || '';
        const isCurrentEvent = checkChannelFormCurrentEvent(
          streamChannelId,
          currentEventId,
        );

        if (!isCurrentEvent) {
          return;
        }

        const isEventChannel = checkIsEventChannel(
          streamChannelId,
          currentEventId,
        );

        if (isEventChannel) {
          if (hasEventStreamChannel) {
            return;
          }

          const eventStreamChannel = await getEventStreamChannel({
            streamClient,
            userId: userId,
            eventId: currentEventId,
          });

          setEventStreamChannel(eventStreamChannel);
          return;
        }

        const privateChannelId = getPrivateChannelId(streamChannelId);
        const eventChannel = await ChannelService.getEventChannel(
          currentEventId,
          privateChannelId,
        );
        const newPrivateChannel = await getMapEventChannel(
          streamClient,
          eventChannel,
          userId,
        );

        addPrivateChannel(newPrivateChannel);
      },
    );

    return () => {
      newChannelSubscription.unsubscribe();
    };
  }, [streamClient, currentEventId, userId, hasEventStreamChannel]);
};

export default useStreamChatHandlers;
